/**
 * "Wie lese ich diesen Bericht?" — first-class, not a tooltip afterthought.
 *
 * The reader is an architect, not an AI person: this panel explains the traffic
 * lights, what a confidence tag means, what the numbers on a card are, why some
 * rules did not run, what indicative output is, what a vorbehaltener Nachweis
 * is, and where the liability sits. No AI vocabulary, no accuracy figure — the
 * only honest accuracy numbers we have come from a single decided case, and a
 * loose figure in the product is worse than none (see decisions.md,
 * "Eine Eval-Zahl, ein Lauf").
 *
 * Two rendering modes, one content model:
 *   · screen — a disclosure with a real button (`aria-expanded` / `aria-controls`),
 *     open by default, keyboard-complete;
 *   · print  — the same topics, always expanded, no control.
 */
import { useId, useState } from 'react'
import { BookOpenText, ChevronDown } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { EXPLAINER_TOPICS } from '@/features/report/explainerTopics'
import { useI18n } from '@/i18n'

export function Explainer({ variant = 'screen' }: { variant?: 'screen' | 'print' }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(true)
  const bodyId = useId()
  const titleId = useId()
  const isPrint = variant === 'print'
  const expanded = isPrint || open

  return (
    <section
      data-testid="explainer"
      aria-labelledby={titleId}
      className="flex flex-col gap-4 rounded-md border border-border bg-accent-soft p-6 break-inside-avoid"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Icon icon={BookOpenText} className="mt-1 size-5 text-accent" />
          <div>
            <h2 id={titleId} className="font-display text-[21px] font-semibold">
              {t('explainer.title')}
            </h2>
            <p className="mt-1 max-w-[80ch] text-[13px] text-muted">{t('explainer.lead')}</p>
          </div>
        </div>
        {isPrint ? null : (
          <button
            type="button"
            data-testid="explainer-toggle"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex items-center gap-1.5 rounded-sm border border-border-strong bg-white px-3 py-1.5 text-[13px] text-ink-2 transition-[transform,opacity] duration-fast hover:opacity-90"
          >
            <Icon
              icon={ChevronDown}
              className={open ? 'rotate-180 transition-transform duration-fast' : 'transition-transform duration-fast'}
            />
            {open ? t('explainer.hide') : t('explainer.show')}
          </button>
        )}
      </div>

      <div id={bodyId} hidden={!expanded} data-testid="explainer-body">
        <dl className="grid gap-5 md:grid-cols-2">
          {EXPLAINER_TOPICS.map((topic) => (
            <div key={topic.id} data-explainer-topic={topic.id} className="break-inside-avoid">
              <dt className="font-display text-[15px] font-semibold text-ink">{t(topic.title)}</dt>
              {topic.body.map((line) => (
                <dd key={line} className="mt-1 max-w-[80ch] text-[13px] leading-relaxed text-ink-2">
                  {t(line)}
                </dd>
              ))}
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}
